import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, useNavigate, useParams } from "react-router";
import { v4 } from "uuid";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Copy,
  Loader2,
  MessageSquareMore,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import t from "@/lang/lang";
import {
  useGfsAssessmentGetQuery,
  useGfsAssessmentListQuery,
} from "@/services/backend/gfs/assessment/api";
import { useGfsCatalogGetQuery } from "@/services/backend/gfs/catalog/api";
import {
  formatGfsConsumableDisplayName,
  GfsCatalog,
} from "@/services/backend/gfs/catalog/types";
import { formatMonitorType } from "@/services/backend/pestcontrol/lib/monitor";
import { PestcontrolMonitorType } from "@/services/backend/pestcontrol/types/monitor";
import {
  useGfsCalculationDeleteMutation,
  useGfsCalculationGetQuery,
  useGfsCalculationLoadAssessmentMutation,
  useGfsCalculationPutMutation,
  updateGfsCalculationCache,
} from "@/services/backend/gfs/calculation/api";
import {
  GfsCalculation,
  GfsMeasure,
  GfsMeasureKind,
  GfsMeasurePeriodUnit,
  GfsMeasureSubdivision,
  GfsMeasureSettings,
  GfsMeasurePricing,
  GfsPriceItem,
  GfsPriceItemKind,
  GfsPriceItemPricingType,
  GfsPricing,
  GfsPricingSettings,
  PutGfsCalculationRequest,
} from "@/services/backend/gfs/calculation/types";
import {
  formatGfsUnit,
  GfsPriceUnitOptions,
  GfsTimePriceUnitOptions,
  GfsUnit,
  GfsUnitValue,
  normalizeGfsUnit,
} from "@/services/backend/gfs/unit";
import {
  FloatNumberInput,
  IntNumberInput,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { TextareaDebounced } from "@/shared/components/inputs/textarea-debounced";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableCellSelect,
  ETableFooter,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";
import { cn } from "@/shared/lib/utils";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import type { RtkQueryMutation } from "@/shared/lib/debounce/debounce";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import {
  PersistenceStateInfo,
  PersistenceStateUsingContext,
} from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/shared/components/ui/toggle-group";
import { toast } from "sonner";
import { formatDate } from "@/shared/lib/utilities/date";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { SetGfsCalculationImmutableButton } from "@/services/backend/gfs/calculation/components/set-calculation-immutable-button";

const PRICE_ITEM_KINDS: {
  value: GfsPriceItemKind;
  label: string;
}[] = [
  { value: "labor", label: "Arbeitszeit" },
  { value: "travel", label: "Fahrzeit" },
  { value: "hotel", label: "Übernachtung" },
  { value: "allowance", label: "Spesen" },
  { value: "material", label: "Material" },
  { value: "service", label: "Service" },
  { value: "monitor", label: "Monitor/Falle" },
  { value: "consumable", label: "Verbrauchsmaterial" },
  { value: "surcharge", label: "Prozentualer Aufschlag" },
  { value: "other", label: "Sonstiges" },
];

const PRICE_ITEM_UNIT_OPTIONS = GfsPriceUnitOptions.map(
  (option) => option.value,
);

const TIME_PRICE_ITEM_UNIT_OPTIONS = GfsTimePriceUnitOptions.map(
  (option) => option.value,
);

const FREE_TEXT_CATALOG_VALUE = "__free_text__";
const NO_UNIT_SELECT_VALUE = "none";
const UNIT_SUBDIVISION_DEFAULT_LABEL =
  "Für zusätzliche Leistungen berechnen wir:";

const MEASURE_PERIOD_UNITS: {
  value: GfsMeasurePeriodUnit;
  label: string;
}[] = [
  { value: "WEEK", label: "Woche" },
  { value: "MONTH", label: "Monat" },
  { value: "YEAR", label: "Jahr" },
];

type CalculationPersistenceSection = "measures" | "pricing" | "priceItems";

export function GfsCalculationEditor() {
  const { calculationId = "" } = useParams();
  return (
    <PersistenceContextProvider>
      <CalculationEditor calculationId={calculationId} key={calculationId} />
    </PersistenceContextProvider>
  );
}

function CalculationEditor({ calculationId }: { calculationId: string }) {
  const { processId = "" } = useParams();
  const {
    data: calculation,
    error,
    isFetching,
  } = useGfsCalculationGetQuery(
    { id: calculationId },
    { skip: !calculationId },
  );
  const { persistenceState } = usePersistenceContext();
  const { data: catalog } = useGfsCatalogGetQuery(
    { id: calculation?.catalogId ?? "" },
    { skip: !calculation?.catalogId },
  );
  const initialPutRequest = useMemo(
    () => (calculation ? toPutRequest(calculation) : undefined),
    [calculation],
  );
  const [saveMutation, saveState] = useGfsCalculationPutMutation();
  const typedSaveMutation = [
    saveMutation,
    saveState,
  ] as unknown as RtkQueryMutation<PutGfsCalculationRequest, GfsCalculation>;
  const [setCalculation] = useOptimisticDebouncedMutation<
    GfsCalculation,
    PutGfsCalculationRequest,
    GfsCalculation
  >(
    initialPutRequest ?? emptyPutRequest(calculationId),
    updateGfsCalculationCache,
    typedSaveMutation,
    { delay: 500, toastError: true },
  );

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (!calculation || !initialPutRequest) return null;

  const displayAssessmentId =
    calculation.sourceAssessmentId || calculation.assessment.sourceId;
  const isPersistencePending = persistenceState === PersistenceState.SAVING;
  const editorDisabled = saveState.isLoading || isFetching;

  const updateCalculation = (nextValue: GfsCalculationPricingValue) => {
    const nextRequest: PutGfsCalculationRequest = {
      id: calculation.id,
      ...nextValue,
    };
    setCalculation(
      nextRequest,
      toOptimisticCalculation(calculation, nextRequest),
    );
  };

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageActions>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm">{t("Basis-Bestandsaufnahme")}:</span>
            <SelectAssessmentButton
              calculation={calculation}
              calculationId={calculation.id}
              disabled={isPersistencePending || isFetching}
              processId={processId}
            />
          </div>
          <PersistenceStateUsingContext className="mr-2" />
          <SetGfsCalculationImmutableButton
            calculationId={calculation.id}
            immutable={calculation.immutable}
          />
          <DeleteCalculationDialog
            calculationId={calculation.id}
            disabled={isPersistencePending || isFetching}
          />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        {saveState.error && <RTKQueryErrorAlert error={saveState.error} />}
        <div className="space-y-5 pb-16">
          <AssessmentSnapshotSummary
            calculation={calculation}
            displayAssessmentId={displayAssessmentId}
            processId={processId}
          />
          <GfsMeasurePricingEditor
            disabled={editorDisabled}
            value={initialPutRequest}
            pricing={calculation.pricing}
            catalog={catalog}
            saveState={saveState}
            onChange={updateCalculation}
          />
        </div>
      </DocumentPageContent>
    </DocumentPage>
  );
}

export type GfsCalculationPricingValue = Pick<
  PutGfsCalculationRequest,
  "measureSettings" | "pricingSettings" | "priceItems"
> & {
  measures: GfsMeasure[];
};

type GfsCalculationPricingSaveState = {
  isLoading?: boolean;
  isSuccess?: boolean;
  successKey?: unknown;
  error?: unknown;
};

export function GfsMeasurePricingEditor({
  catalog,
  pricing,
  value,
  disabled = false,
  saveState,
  onChange,
}: {
  catalog?: GfsCatalog;
  pricing: GfsPricing;
  value: GfsCalculationPricingValue;
  disabled?: boolean;
  saveState?: GfsCalculationPricingSaveState;
  onChange: (value: GfsCalculationPricingValue) => void;
}) {
  const [sectionPersistenceStates, setSectionPersistenceStates] = useState<
    Record<CalculationPersistenceSection, PersistenceState>
  >({
    measures: PersistenceState.SAVED,
    pricing: PersistenceState.SAVED,
    priceItems: PersistenceState.SAVED,
  });
  const flexibleMeasures = value.measures;
  const valueRef = useRef(value);
  valueRef.current = value;
  const saveSuccess = saveState?.successKey ?? saveState?.isSuccess;
  const isCalculationUpdating =
    saveState?.isLoading === true ||
    Object.values(sectionPersistenceStates).some(
      (state) => state === PersistenceState.SAVING,
    );

  useEffect(() => {
    if (!saveSuccess) {
      return;
    }
    setSectionPersistenceStates((current) =>
      updateSavingSections(current, PersistenceState.SAVED),
    );
  }, [saveSuccess]);

  useEffect(() => {
    if (!saveState?.error) {
      return;
    }
    setSectionPersistenceStates((current) =>
      updateSavingSections(current, PersistenceState.ERROR),
    );
  }, [saveState?.error]);

  const markSectionSaving = (section: CalculationPersistenceSection) => {
    setSectionPersistenceStates((current) => ({
      ...current,
      [section]: PersistenceState.SAVING,
    }));
  };

  const updateMeasures = (measures: GfsMeasure[]) => {
    if (disabled) return;
    markSectionSaving("measures");
    const nextValue = {
      ...valueRef.current,
      measures,
    };
    valueRef.current = nextValue;
    onChange(nextValue);
  };

  const updatePricingSettings = (settings: GfsPricingSettings) => {
    if (disabled) return;
    markSectionSaving("pricing");
    const nextValue = {
      ...valueRef.current,
      pricingSettings: settings,
    };
    valueRef.current = nextValue;
    onChange(nextValue);
  };

  const updateMeasureSettings = (settings: GfsMeasureSettings) => {
    if (disabled) return;
    markSectionSaving("measures");
    const nextValue = {
      ...valueRef.current,
      measureSettings: settings,
    };
    valueRef.current = nextValue;
    onChange(nextValue);
  };

  return (
    <>
      <MeasureSettingsSection
        disabled={disabled}
        measureCount={pricing.measureCount}
        measureSettings={value.measureSettings}
        persistenceState={sectionPersistenceStates.measures}
        updateMeasureSettings={updateMeasureSettings}
      />
      <PricingSettingsSection
        disabled={disabled}
        persistenceState={sectionPersistenceStates.pricing}
        pricingSettings={value.pricingSettings}
        updatePricingSettings={updatePricingSettings}
      />
      <CalculationTotalCard
        isUpdating={isCalculationUpdating}
        pricing={pricing}
      />
      <FlexibleMeasuresSection
        catalog={catalog}
        disabled={disabled}
        isUpdating={isCalculationUpdating}
        measures={flexibleMeasures}
        persistenceState={sectionPersistenceStates.measures}
        pricing={pricing}
        pricingSettings={value.pricingSettings}
        updateMeasures={updateMeasures}
      />
    </>
  );
}

function FlexibleMeasuresSection({
  catalog,
  disabled,
  isUpdating,
  measures,
  pricing,
  persistenceState,
  pricingSettings,
  updateMeasures,
}: {
  catalog?: GfsCatalog;
  disabled?: boolean;
  isUpdating: boolean;
  measures: GfsMeasure[];
  pricing: GfsPricing;
  persistenceState: PersistenceState;
  pricingSettings: GfsPricingSettings;
  updateMeasures: (measures: GfsMeasure[]) => void;
}) {
  const measuresRef = useRef(measures);
  measuresRef.current = measures;

  const updateMeasure = (index: number, patch: Partial<GfsMeasure>) => {
    const nextMeasures = measuresRef.current.map((measure, currentIndex) =>
      currentIndex === index ? { ...measure, ...patch } : measure,
    );
    measuresRef.current = nextMeasures;
    updateMeasures(nextMeasures);
  };

  const addMeasure = () => {
    const currentMeasures = measuresRef.current;
    const finalIndex = currentMeasures.findIndex(
      (measure) => measure.kind === "FINAL",
    );
    const nextMeasure = defaultFlexibleMeasure("INTERMEDIATE");
    if (finalIndex < 0) {
      const nextMeasures = [...currentMeasures, nextMeasure];
      measuresRef.current = nextMeasures;
      updateMeasures(nextMeasures);
      return;
    }
    const nextMeasures = [
      ...currentMeasures.slice(0, finalIndex),
      nextMeasure,
      ...currentMeasures.slice(finalIndex),
    ];
    measuresRef.current = nextMeasures;
    updateMeasures(nextMeasures);
  };

  const removeMeasure = (index: number) => {
    const nextMeasures = measuresRef.current.filter(
      (_measure, currentIndex) => currentIndex !== index,
    );
    measuresRef.current = nextMeasures;
    updateMeasures(nextMeasures);
  };

  return (
    <section className="space-y-5">
      <SectionHeader
        title={t("Maßnahmen")}
        description={t(
          "Gleiche aufeinanderfolgende Maßnahmen werden zu einem Block zusammengefasst. Eine Tilgungskontrolle bezieht sich immer auf die letzte konkrete Maßnahme.",
        )}
        persistenceState={persistenceState}
      />
      <div className="space-y-3">
        {measures.map((measure, index) => (
          <FlexibleMeasureCard
            catalog={catalog}
            key={measure.id}
            calculatedMeasure={pricing.measures?.[index]}
            disabled={disabled}
            isUpdating={isUpdating}
            measure={measure}
            measureNumber={index + 1}
            isLastMeasure={index === measures.length - 1}
            pricingSettings={pricingSettings}
            onChange={(patch) => updateMeasure(index, patch)}
            onRemove={() => removeMeasure(index)}
          />
        ))}
      </div>
      <div className="flex justify-start">
        <Button variant="outline" disabled={disabled} onClick={addMeasure}>
          <Plus className="h-4 w-4" />
          {t("Maßnahme hinzufügen")}
        </Button>
      </div>
    </section>
  );
}

function CalculationTotalCard({
  isUpdating,
  pricing,
}: {
  isUpdating: boolean;
  pricing: GfsPricing;
}) {
  return (
    <section className="bg-primary/5 ring-primary/20 flex items-center justify-between gap-4 rounded-lg px-6 py-4 shadow-sm ring-1">
      <div>
        <h2 className="text-sm font-semibold">
          {t("Gesamtkosten der Kalkulation")}
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          {t("Gesamtpreis über alle Maßnahmen")}
        </p>
      </div>
      <div className="text-right text-xl font-semibold tabular-nums">
        <UpdatingValue isUpdating={isUpdating}>
          {formatCurrency(pricing.totalCost)}
        </UpdatingValue>
      </div>
    </section>
  );
}

function FlexibleMeasureCard({
  catalog,
  calculatedMeasure,
  disabled,
  isUpdating,
  measure,
  measureNumber,
  isLastMeasure,
  onChange,
  onRemove,
  pricingSettings,
}: {
  catalog?: GfsCatalog;
  calculatedMeasure?: GfsMeasurePricing;
  disabled?: boolean;
  isUpdating: boolean;
  measure: GfsMeasure;
  measureNumber: number;
  isLastMeasure: boolean;
  onChange: (patch: Partial<GfsMeasure>) => void;
  onRemove: () => void;
  pricingSettings: GfsPricingSettings;
}) {
  const subdivisions = measure.subdivisions ?? [];
  const isSplit = subdivisions.length > 0;
  const effectivePricingSettings =
    measure.pricingSettingsOverride ?? pricingSettings;
  const priceItemsTitle =
    measure.eradicationCheck && isLastMeasure
      ? t("Preispositionen der Tilgungskontrolle")
      : t("Leistungen der Maßnahme");

  return (
    <div className="bg-card ring-border/60 space-y-4 rounded-lg p-5 shadow-sm ring-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">
          {t("Maßnahme")} {measureNumber}
        </h3>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-muted-foreground text-xs">
              {isSplit ? t("Gesamtpreis je Maßnahme") : t("Preis je Maßnahme")}
            </div>
            <div className="font-semibold">
              <UpdatingValue isUpdating={isUpdating}>
                {formatCurrency(calculatedMeasure?.pricePerMeasure ?? 0)}
              </UpdatingValue>
            </div>
          </div>
          <Button
            aria-label={t("Maßnahme entfernen")}
            disabled={disabled}
            onClick={onRemove}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <InlineControl label={t("Art")}>
          <Select
            disabled={disabled || measureNumber === 1}
            value={measureNumber === 1 ? "INITIAL" : measure.kind}
            onValueChange={(kind) =>
              onChange({
                kind: kind as GfsMeasureKind,
                repeatCount: kind === "INTERMEDIATE" ? measure.repeatCount : 1,
              })
            }
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {measureNumber === 1 && (
                <SelectItem value="INITIAL">{t("Erstmaßnahme")}</SelectItem>
              )}
              <SelectItem value="INTERMEDIATE">
                {t("Zwischenmaßnahme")}
              </SelectItem>
              <SelectItem value="FINAL">{t("Letzte Maßnahme")}</SelectItem>
            </SelectContent>
          </Select>
        </InlineControl>
        <InlineControl label={t("Bezeichnung")}>
          <InputDebounced
            className="w-56"
            disabled={disabled}
            value={measure.label}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </InlineControl>
        <InlineControl label={t("Anzahl")}>
          <IntNumberInput
            disabled={disabled}
            min={1}
            value={measure.repeatCount}
            onChange={(repeatCount) => onChange({ repeatCount })}
          />
        </InlineControl>
        {measureNumber > 1 && (
          <>
            <InlineControl label={t("Intervall")}>
              <IntNumberInput
                disabled={disabled}
                min={0}
                value={measure.repeatIntervalValue}
                onChange={(repeatIntervalValue) =>
                  onChange({ repeatIntervalValue })
                }
              />
            </InlineControl>
            <Select
              disabled={disabled}
              value={measure.repeatIntervalUnit || "none"}
              onValueChange={(value) =>
                onChange({
                  repeatIntervalUnit:
                    value === "none" ? "" : (value as GfsMeasurePeriodUnit),
                })
              }
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder={t("Einheit")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("Keine Angabe")}</SelectItem>
                {MEASURE_PERIOD_UNITS.map((period) => (
                  <SelectItem key={period.value} value={period.value}>
                    {t(period.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <InlineControl label={t("Servicetechniker")}>
          <IntNumberInput
            disabled={disabled}
            min={0}
            value={measure.technicians ?? 0}
            onChange={(technicians) => onChange({ technicians })}
          />
        </InlineControl>
        <InlineControl label={t("Zeitfenster je Maßnahme")}>
          <TimeNumberInput
            disabled={disabled}
            value={measure.duration ?? 0}
            onChange={(duration) => onChange({ duration })}
          />
        </InlineControl>
      </div>
      <div className="bg-muted/20 rounded-lg px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {!isSplit && (
            <MeasureUnitPriceSetting
              disabled={disabled}
              value={measure.useUnitPrice}
              onChange={(useUnitPrice) => onChange({ useUnitPrice })}
            />
          )}
          <MeasurePricingOverride
            disabled={disabled}
            pricingSettings={pricingSettings}
            value={measure.pricingSettingsOverride}
            onChange={(pricingSettingsOverride) =>
              onChange({ pricingSettingsOverride })
            }
          />
          <MeasureEradicationCheckSetting
            checked={measure.eradicationCheck && isLastMeasure}
            disabled={disabled || !isLastMeasure}
            onChange={(eradicationCheck) => onChange({ eradicationCheck })}
          />
        </div>
        {measure.pricingSettingsOverride && (
          <MeasurePricingSettingsFields
            disabled={disabled}
            settings={measure.pricingSettingsOverride}
            onChange={(settings) =>
              onChange({ pricingSettingsOverride: settings })
            }
          />
        )}
      </div>
      {isSplit ? (
        <MeasureSubdivisionsEditor
          catalog={catalog}
          calculatedSubdivisions={calculatedMeasure?.subdivisions ?? []}
          disabled={disabled}
          isUpdating={isUpdating}
          measureCount={measure.repeatCount}
          pricingSettings={effectivePricingSettings}
          subdivisions={subdivisions}
          onChange={(nextSubdivisions) =>
            onChange({ subdivisions: nextSubdivisions, useUnitPrice: false })
          }
          onCollapse={() =>
            onChange({
              priceItems: subdivisions.flatMap(
                (subdivision) => subdivision.priceItems,
              ),
              subdivisions: [],
              useUnitPrice: false,
            })
          }
        />
      ) : (
        <div className="space-y-3">
          <FlexiblePriceItemsTable
            catalog={catalog}
            calculatedItems={calculatedMeasure?.priceItems ?? []}
            calculatedPricePerMeasure={calculatedMeasure?.pricePerMeasure ?? 0}
            calculatedTotalCost={calculatedMeasure?.totalCost ?? 0}
            disabled={disabled}
            isUpdating={isUpdating}
            items={measure.priceItems ?? []}
            measureCount={measure.repeatCount}
            pricingSettings={effectivePricingSettings}
            title={priceItemsTitle}
            onChange={(priceItems) => onChange({ priceItems })}
          />
          <Button
            disabled={disabled}
            onClick={() =>
              onChange({
                priceItems: [],
                subdivisions: [
                  {
                    id: v4(),
                    label: "",
                    pricingType: "CALCULATED",
                    priceItems: (measure.priceItems ?? []).map((item) =>
                      item.kind === "surcharge"
                        ? item
                        : { ...item, pricingType: "CALCULATED" },
                    ),
                  },
                  { id: v4(), label: "", priceItems: [] },
                ],
                useUnitPrice: false,
              })
            }
            size="sm"
            type="button"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            {t("Maßnahme gliedern")}
          </Button>
        </div>
      )}
    </div>
  );
}

function MeasureSubdivisionsEditor({
  calculatedSubdivisions,
  catalog,
  disabled,
  isUpdating,
  measureCount,
  onChange,
  onCollapse,
  pricingSettings,
  subdivisions,
}: {
  calculatedSubdivisions: NonNullable<GfsMeasurePricing["subdivisions"]>;
  catalog?: GfsCatalog;
  disabled?: boolean;
  isUpdating: boolean;
  measureCount: number;
  onChange: (subdivisions: GfsMeasureSubdivision[]) => void;
  onCollapse: () => void;
  pricingSettings: GfsPricingSettings;
  subdivisions: GfsMeasureSubdivision[];
}) {
  const subdivisionsRef = useRef(subdivisions);
  subdivisionsRef.current = subdivisions;

  const updateSubdivision = (
    index: number,
    patch: Partial<GfsMeasureSubdivision>,
  ) => {
    const nextSubdivisions = subdivisionsRef.current.map(
      (subdivision, currentIndex) =>
        currentIndex === index ? { ...subdivision, ...patch } : subdivision,
    );
    subdivisionsRef.current = nextSubdivisions;
    onChange(nextSubdivisions);
  };

  const addSubdivision = () => {
    const nextSubdivisions = [
      ...subdivisionsRef.current,
      {
        id: v4(),
        label: "",
        pricingType: "CALCULATED" as const,
        priceItems: [],
      },
    ];
    subdivisionsRef.current = nextSubdivisions;
    onChange(nextSubdivisions);
  };

  const moveSubdivision = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    const currentSubdivisions = subdivisionsRef.current;
    if (
      targetIndex < 0 ||
      targetIndex >= currentSubdivisions.length ||
      index < 0 ||
      index >= currentSubdivisions.length
    ) {
      return;
    }
    const nextSubdivisions = [...currentSubdivisions];
    [nextSubdivisions[index], nextSubdivisions[targetIndex]] = [
      nextSubdivisions[targetIndex],
      nextSubdivisions[index],
    ];
    subdivisionsRef.current = nextSubdivisions;
    onChange(nextSubdivisions);
  };

  const updateSubdivisionPricingType = (
    index: number,
    pricingType: GfsPriceItemPricingType,
  ) => {
    const currentSubdivision = subdivisionsRef.current[index];
    const currentLabel = currentSubdivision.label.trim();
    const currentPricingType = currentSubdivision.pricingType ?? "CALCULATED";
    const label =
      currentPricingType === "CALCULATED" && pricingType === "UNIT"
        ? UNIT_SUBDIVISION_DEFAULT_LABEL
        : pricingType === "CALCULATED" &&
            currentLabel === UNIT_SUBDIVISION_DEFAULT_LABEL
          ? ""
          : currentSubdivision.label;
    updateSubdivision(index, {
      label,
      pricingType,
      priceItems: currentSubdivision.priceItems.map((item) =>
        item.kind === "surcharge" ? item : { ...item, pricingType },
      ),
    });
  };

  const removeSubdivision = (index: number) => {
    if (subdivisionsRef.current.length <= 2) {
      onCollapse();
      return;
    }
    const nextSubdivisions = subdivisionsRef.current.filter(
      (_subdivision, currentIndex) => currentIndex !== index,
    );
    subdivisionsRef.current = nextSubdivisions;
    onChange(nextSubdivisions);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-sm font-semibold">
          {t("Unterteilung der Maßnahme")}
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={disabled}
            onClick={addSubdivision}
            size="sm"
            type="button"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            {t("Teil der Maßnahme hinzufügen")}
          </Button>
          <Button
            disabled={disabled}
            onClick={onCollapse}
            size="sm"
            type="button"
            variant="ghost"
          >
            {t("Aufteilung aufheben")}
          </Button>
        </div>
      </div>
      {subdivisions.map((subdivision, index) => (
        <div className="space-y-3 rounded-lg border p-3" key={subdivision.id}>
          <div className="flex flex-wrap items-end gap-3">
            <label className="min-w-0 flex-1 space-y-1.5 text-sm font-medium">
              <span>{t("Bezeichner des Maßnahmenteils")}</span>
              <InputDebounced
                disabled={disabled}
                placeholder={t("Optionale gemeinsame Bezeichnung im Angebot")}
                value={
                  subdivision.pricingType === "UNIT" &&
                  subdivision.label.trim() === ""
                    ? UNIT_SUBDIVISION_DEFAULT_LABEL
                    : subdivision.label
                }
                onChange={(event) =>
                  updateSubdivision(index, { label: event.target.value })
                }
              />
            </label>
            <label className="space-y-1.5 text-sm font-medium">
              <span>{t("Preisart")}</span>
              <Select
                disabled={disabled}
                value={subdivision.pricingType ?? "CALCULATED"}
                onValueChange={(pricingType) =>
                  updateSubdivisionPricingType(
                    index,
                    pricingType as GfsPriceItemPricingType,
                  )
                }
              >
                <SelectTrigger className="w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CALCULATED">
                    {t("Berechneter Preis")}
                  </SelectItem>
                  <SelectItem value="UNIT">{t("Preis je Einheit")}</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("Teil der Maßnahme nach oben verschieben")}
                    disabled={disabled || index === 0}
                    onClick={() => moveSubdivision(index, -1)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("Eine Position nach oben verschieben")}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("Teil der Maßnahme nach unten verschieben")}
                    disabled={disabled || index === subdivisions.length - 1}
                    onClick={() => moveSubdivision(index, 1)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("Eine Position nach unten verschieben")}
                </TooltipContent>
              </Tooltip>
              <Button
                aria-label={t("Teil der Maßnahme entfernen")}
                disabled={disabled}
                onClick={() => removeSubdivision(index)}
                size="icon"
                type="button"
                variant="ghost"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <FlexiblePriceItemsTable
            catalog={catalog}
            calculatedItems={calculatedSubdivisions[index]?.priceItems ?? []}
            calculatedPricePerMeasure={
              calculatedSubdivisions[index]?.pricePerMeasure ?? 0
            }
            calculatedTotalCost={calculatedSubdivisions[index]?.totalCost ?? 0}
            disabled={disabled}
            isUpdating={isUpdating}
            items={subdivision.priceItems ?? []}
            allowUnitPrice={false}
            measureCount={measureCount}
            pricingSettings={pricingSettings}
            pricingType={subdivision.pricingType ?? "CALCULATED"}
            title={t("Preispositionen")}
            onChange={(priceItems) => updateSubdivision(index, { priceItems })}
          />
        </div>
      ))}
    </div>
  );
}

function MeasurePricingOverride({
  disabled,
  onChange,
  pricingSettings,
  value,
}: {
  disabled?: boolean;
  onChange: (value: GfsPricingSettings | undefined) => void;
  pricingSettings: GfsPricingSettings;
  value?: GfsPricingSettings;
}) {
  const enabled = Boolean(value);

  return (
    <div className="flex items-center gap-2 text-sm font-medium">
      <Checkbox
        aria-label={t("Individuelle Preisberechnung")}
        checked={enabled}
        disabled={disabled}
        onCheckedChange={(checked) =>
          onChange(checked === true ? pricingSettings : undefined)
        }
      />
      <span>{t("Individuelle Preisberechnung")}</span>
      <InfoIconTooltip
        text={t(
          "Wenn aktiviert, können Preis je Minute und Zielumsatz pro Stunde für diese Maßnahme individuell festgelegt werden.",
        )}
      />
    </div>
  );
}

function MeasurePricingSettingsFields({
  disabled,
  onChange,
  settings,
}: {
  disabled?: boolean;
  onChange: (settings: GfsPricingSettings) => void;
  settings: GfsPricingSettings;
}) {
  return (
    <div className="mt-2 ml-6 flex flex-wrap items-center gap-x-8 gap-y-3">
      <InlineControl label={t("Preis je Minute")}>
        <FloatNumberInput
          disabled={disabled}
          fractions={4}
          value={settings.laborTravelFactor}
          onChange={(laborTravelFactor) =>
            onChange(pricingSettingsFromFactor(settings, laborTravelFactor))
          }
        />
      </InlineControl>
      <InlineControl label={t("Zielumsatz pro Stunde")}>
        <div className="flex items-center gap-2">
          <FloatNumberInput
            disabled={disabled}
            value={settings.targetRevenuePerHour}
            onChange={(targetRevenuePerHour) =>
              onChange(
                pricingSettingsFromTargetRevenue(
                  settings,
                  targetRevenuePerHour,
                ),
              )
            }
          />
          <span className="text-muted-foreground text-sm">€</span>
        </div>
      </InlineControl>
    </div>
  );
}

function MeasureUnitPriceSetting({
  disabled,
  onChange,
  value,
}: {
  disabled?: boolean;
  onChange: (value: boolean) => void;
  value: boolean;
}) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium">
      <Checkbox
        aria-label={t("Preisart: ausschließlich Preis je Einheit")}
        checked={value}
        disabled={disabled}
        onCheckedChange={(checked) => onChange(checked === true)}
      />
      <span>{t("Preisart: ausschließlich Preis je Einheit")}</span>
      <InfoIconTooltip
        text={t(
          "Wenn aktiviert, wird für alle Preispositionen der Preis je Einheit verwendet.",
        )}
      />
    </div>
  );
}

function MeasureEradicationCheckSetting({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium">
      <Checkbox
        aria-label={t("Tilgungskontrolle")}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(nextChecked) => onChange(nextChecked === true)}
      />
      <span>{t("Tilgungskontrolle")}</span>
      <InfoIconTooltip
        text={t(
          "Wenn aktiviert, wird die letzte konkrete Maßnahme dieses Blocks als Tilgungskontrolle verwendet.",
        )}
      />
    </div>
  );
}

function FlexiblePriceItemsTable({
  allowUnitPrice = true,
  catalog,
  calculatedItems,
  calculatedPricePerMeasure,
  calculatedTotalCost,
  disabled,
  isUpdating,
  items,
  measureCount,
  onChange,
  pricingSettings,
  pricingType,
  title,
}: {
  allowUnitPrice?: boolean;
  catalog?: GfsCatalog;
  calculatedItems: GfsPriceItem[];
  calculatedPricePerMeasure: number;
  calculatedTotalCost: number;
  disabled?: boolean;
  isUpdating: boolean;
  items: GfsPriceItem[];
  measureCount: number;
  onChange: (items: GfsPriceItem[]) => void;
  pricingSettings: GfsPricingSettings;
  pricingType?: GfsPriceItemPricingType;
  title: string;
}) {
  const updateItem = (index: number, patch: Partial<GfsPriceItem>) => {
    onChange(
      items.map((item, currentIndex) =>
        currentIndex === index ? { ...item, ...patch } : item,
      ),
    );
  };

  const addItem = () => {
    onChange([...items, defaultFlexiblePriceItem()]);
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const nextItems = [...items];
    [nextItems[index], nextItems[targetIndex]] = [
      nextItems[targetIndex],
      nextItems[index],
    ];
    onChange(nextItems);
  };

  const changeItemKind = (index: number, kind: GfsPriceItemKind) => {
    const currentItem = items[index];
    if (!currentItem) return;
    onChange(
      items.map((item, currentIndex) =>
        currentIndex === index
          ? defaultFlexiblePriceItemForKind(kind, currentItem.id)
          : item,
      ),
    );
  };

  const selectCatalogMonitor = (index: number, catalogItemId: string) => {
    const monitor = catalog?.items.find((item) => item.id === catalogItemId);
    if (!monitor || !catalog) return;
    const next = items.map((item, currentIndex) =>
      currentIndex === index
        ? {
            ...item,
            kind: "monitor" as const,
            label: monitor.monitor.name,
            unit: GfsUnit.Piece,
            unitPrice: monitor.price,
            unitPriceManual: false,
            sourceCatalogId: catalog.id,
            catalogItemId: monitor.id,
            catalogConsumableId: undefined,
            catalogPests: monitor.pests,
            catalogGroups: monitor.groups,
            catalogTakenAt: undefined,
          }
        : item,
    );
    const selected = next[index];
    monitor.consumables
      .filter((link) => link.mode === "REFERENCED")
      .forEach((link) => {
        if (next.some((item) => item.catalogConsumableId === link.consumableId))
          return;
        const consumable = catalog.consumables.find(
          (candidate) => candidate.id === link.consumableId,
        );
        if (!consumable) return;
        next.push({
          ...defaultFlexiblePriceItem(),
          kind: "consumable",
          label: consumable.name,
          quantity: link.quantity * selected.quantity,
          unit: consumable.unit,
          unitPrice: link.unitPriceOverride ?? consumable.unitPrice,
          sourceCatalogId: catalog.id,
          catalogConsumableId: consumable.id,
          productDisplayText: consumable.productDisplayText,
          catalogTakenAt: undefined,
        });
      });
    onChange(next);
  };

  const setManualMonitor = (index: number, label: string) => {
    const item = items[index];
    const hasCatalogData = Boolean(
      item?.sourceCatalogId ||
      item?.catalogItemId ||
      item?.catalogConsumableId ||
      item?.catalogTakenAt,
    );
    updateItem(index, {
      label,
      unit: hasCatalogData ? GfsUnit.Piece : item.unit,
      unitPrice: hasCatalogData ? 0 : item.unitPrice,
      unitPriceManual: true,
      sourceCatalogId: undefined,
      catalogItemId: undefined,
      catalogConsumableId: undefined,
      productDisplayText: undefined,
      catalogPests: undefined,
      catalogGroups: undefined,
      catalogTakenAt: undefined,
    });
  };

  const setManualConsumable = (index: number, label: string) => {
    const item = items[index];
    const hasCatalogData = Boolean(
      item?.sourceCatalogId ||
      item?.catalogItemId ||
      item?.catalogConsumableId ||
      item?.catalogTakenAt,
    );
    updateItem(index, {
      label,
      unit: hasCatalogData ? GfsUnit.Piece : item.unit,
      unitPrice: hasCatalogData ? 0 : item.unitPrice,
      unitPriceManual: true,
      sourceCatalogId: undefined,
      catalogItemId: undefined,
      catalogConsumableId: undefined,
      productDisplayText: undefined,
      catalogPests: undefined,
      catalogGroups: undefined,
      catalogTakenAt: undefined,
    });
  };

  const selectCatalogConsumable = (index: number, consumableId: string) => {
    const consumable = catalog?.consumables.find(
      (item) => item.id === consumableId,
    );
    if (!consumable || !catalog) return;
    updateItem(index, {
      kind: "consumable",
      label: consumable.name,
      unit: consumable.unit,
      unitPrice: consumable.unitPrice,
      unitPriceManual: false,
      sourceCatalogId: catalog.id,
      catalogItemId: undefined,
      catalogConsumableId: consumable.id,
      productDisplayText: consumable.productDisplayText,
      catalogPests: [],
      catalogGroups: [],
      catalogTakenAt: undefined,
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold">{title}</h4>
        <Button
          disabled={disabled}
          onClick={addItem}
          size="sm"
          type="button"
          variant="outline"
        >
          <Plus className="h-4 w-4" />
          {t("Position")}
        </Button>
      </div>
      <ETable>
        <ETableHeader>
          <ETableRow>
            <ETableHead>{t("Art")}</ETableHead>
            <ETableHead>{t("Bezeichnung")}</ETableHead>
            <ETableHead>{t("Preisart")}</ETableHead>
            <ETableHead className="text-right">{t("Menge")}</ETableHead>
            <ETableHead>{t("Einheit")}</ETableHead>
            <ETableHead className="text-right">
              {t("Preis je Einheit")}
            </ETableHead>
            <ETableHead className="text-right">{t("Maßnahmen")}</ETableHead>
            <ETableHead className="text-right">
              {t("Preis je Maßnahme")}
            </ETableHead>
            <ETableHead>{t("Kommentar")}</ETableHead>
            <ETableHead />
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {items.map((item, index) => (
            <ETableRow key={item.id}>
              <ETableCellSelect
                label={t("Art")}
                items={PRICE_ITEM_KINDS.map((kind) => ({
                  value: kind.value,
                  label: t(kind.label),
                }))}
                disabled={disabled}
                value={item.kind}
                onValueChange={(kind) =>
                  changeItemKind(index, kind as GfsPriceItemKind)
                }
              />
              {item.kind === "monitor" ? (
                <MonitorPriceItemDescriptionCell
                  catalog={catalog}
                  disabled={disabled}
                  item={item}
                  onCatalogSelect={(catalogItemId) =>
                    selectCatalogMonitor(index, catalogItemId)
                  }
                  onManualSelect={() => setManualMonitor(index, "")}
                  onManualChange={(label) => setManualMonitor(index, label)}
                />
              ) : item.kind === "consumable" ? (
                <ConsumablePriceItemDescriptionCell
                  catalog={catalog}
                  disabled={disabled}
                  item={item}
                  onCatalogSelect={(consumableId) =>
                    selectCatalogConsumable(index, consumableId)
                  }
                  onManualSelect={() => setManualConsumable(index, "")}
                  onManualChange={(label) => setManualConsumable(index, label)}
                />
              ) : (
                <ETableCellInput
                  aria-label={t("Bezeichnung")}
                  disabled={disabled}
                  value={
                    item.label ||
                    (item.kind === "labor" ? t("Arbeitszeit") : "")
                  }
                  onChange={(event) =>
                    updateItem(index, { label: event.target.value })
                  }
                />
              )}
              {item.kind === "surcharge" || !allowUnitPrice ? (
                <ETableCell className="text-muted-foreground text-sm">
                  {t(
                    item.kind !== "surcharge" && pricingType === "UNIT"
                      ? "Preis je Einheit"
                      : "Berechneter Preis",
                  )}
                </ETableCell>
              ) : (
                <ETableCellSelect
                  label={t("Preisart")}
                  items={[
                    { value: "CALCULATED", label: t("Berechneter Preis") },
                    { value: "UNIT", label: t("Preis je Einheit") },
                  ]}
                  disabled={disabled}
                  value={item.pricingType ?? "CALCULATED"}
                  onValueChange={(nextPricingType) =>
                    updateItem(index, {
                      pricingType: nextPricingType as GfsPriceItemPricingType,
                    })
                  }
                />
              )}
              <ETableCellDecimalInput
                aria-label={t("Menge")}
                className="w-full text-right"
                disabled={disabled}
                precision={2}
                value={
                  item.kind === "surcharge"
                    ? (item.percentage ?? 0)
                    : item.quantity
                }
                onValueChange={(quantity) =>
                  updateItem(
                    index,
                    item.kind === "surcharge"
                      ? { percentage: quantity, quantity }
                      : { quantity },
                  )
                }
              />
              {item.kind === "surcharge" ? (
                <ETableCell className="text-muted-foreground text-sm">
                  %
                </ETableCell>
              ) : (
                <ETableCellSelect
                  label={t("Einheit")}
                  items={[
                    { value: NO_UNIT_SELECT_VALUE, label: t("Keine Angabe") },
                    ...priceItemUnitOptions(item).map((unit) => ({
                      value: unit,
                      label: formatGfsUnit(unit),
                    })),
                  ]}
                  disabled={disabled}
                  value={normalizeGfsUnit(item.unit) || NO_UNIT_SELECT_VALUE}
                  onValueChange={(unit) => {
                    const nextUnit =
                      unit === NO_UNIT_SELECT_VALUE ? GfsUnit.None : unit;
                    updateItem(index, {
                      unit: nextUnit,
                      quantity: convertTimePriceItemQuantity(
                        item.quantity,
                        normalizeGfsUnit(item.unit),
                        nextUnit,
                      ),
                    });
                  }}
                />
              )}
              {item.kind === "surcharge" ? (
                <ETableCell className="text-right">-</ETableCell>
              ) : (
                <ETableCellDecimalInput
                  aria-label={t("Preis je Einheit")}
                  className="w-full text-right"
                  disabled={disabled}
                  precision={
                    normalizeGfsUnit(item.unit) === GfsUnit.Minute ? 4 : 2
                  }
                  value={displayedPriceItemUnitPrice(item, pricingSettings)}
                  onValueChange={(unitPrice) =>
                    updateItem(index, { unitPrice, unitPriceManual: true })
                  }
                />
              )}
              <ETableCellInput
                aria-label={t("Maßnahmen")}
                className="w-full text-right"
                disabled={disabled}
                inputMode="numeric"
                pattern="[0-9]*"
                type="text"
                value={String(item.measureCount ?? measureCount)}
                onChange={(event) =>
                  updateItem(index, {
                    measureCount: clampMeasureCount(
                      Number(event.target.value),
                      measureCount,
                    ),
                  })
                }
              />
              <ETableCell className="text-right">
                <UpdatingValue isUpdating={isUpdating}>
                  {formatCurrency(
                    calculatedItems[index]?.displayedPrice ?? item.total,
                  )}
                </UpdatingValue>
              </ETableCell>
              <ETableCell className="text-center">
                <PriceItemCommentButton
                  comment={item.comment}
                  disabled={disabled}
                  label={item.label}
                  onChange={(comment) => updateItem(index, { comment })}
                />
              </ETableCell>
              <ETableCell className="text-center">
                <div className="flex justify-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label={t("Position nach oben verschieben")}
                        disabled={disabled || index === 0}
                        onClick={() => moveItem(index, -1)}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("Eine Position nach oben verschieben")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label={t("Position nach unten verschieben")}
                        disabled={disabled || index === items.length - 1}
                        onClick={() => moveItem(index, 1)}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("Eine Position nach unten verschieben")}
                    </TooltipContent>
                  </Tooltip>
                  <Button
                    aria-label={t("Position entfernen")}
                    disabled={disabled}
                    onClick={() =>
                      onChange(
                        items.filter(
                          (_item, currentIndex) => currentIndex !== index,
                        ),
                      )
                    }
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </ETableCell>
            </ETableRow>
          ))}
        </ETableBody>
        <ETableFooter>
          <ETableRow>
            <ETableCell colSpan={7}>{t("Preis je Maßnahme")}</ETableCell>
            <ETableCell className="text-right">
              <UpdatingValue isUpdating={isUpdating}>
                {formatCurrency(calculatedPricePerMeasure)}
              </UpdatingValue>
            </ETableCell>
            <ETableCell colSpan={2} />
          </ETableRow>
          <ETableRow>
            <ETableCell colSpan={7}>
              {t("Gesamtpreis über alle Maßnahmen")}
            </ETableCell>
            <ETableCell className="text-right">
              <UpdatingValue isUpdating={isUpdating}>
                {formatCurrency(calculatedTotalCost)}
              </UpdatingValue>
            </ETableCell>
            <ETableCell colSpan={2} />
          </ETableRow>
        </ETableFooter>
      </ETable>
    </div>
  );
}

function MonitorPriceItemDescriptionCell({
  catalog,
  disabled,
  item,
  onCatalogSelect,
  onManualSelect,
  onManualChange,
}: {
  catalog?: GfsCatalog;
  disabled?: boolean;
  item: GfsPriceItem;
  onCatalogSelect: (catalogItemId: string) => void;
  onManualSelect: () => void;
  onManualChange: (label: string) => void;
}) {
  const [freeTextSelected, setFreeTextSelected] = useState(
    !item.catalogItemId && Boolean(item.label),
  );

  const selectDescription = (value: string) => {
    if (value === FREE_TEXT_CATALOG_VALUE) {
      setFreeTextSelected(true);
      onManualSelect();
      return;
    }
    setFreeTextSelected(false);
    onCatalogSelect(value);
  };

  return (
    <ETableCell className="px-0">
      <div className="grid min-w-[14rem] gap-1 p-1">
        {!freeTextSelected && (
          <GfsCatalogCombobox
            filterOptions={catalogFilterOptions(
              catalog?.items.map((catalogItem) => catalogItem.monitor.type) ??
                [],
              (value) => formatMonitorType(value as PestcontrolMonitorType),
            )}
            options={
              catalog?.items.map((catalogItem) => ({
                filterKey: catalogItem.monitor.type,
                label: catalogItem.monitor.name,
                price: catalogItem.price,
                unit: GfsUnit.Piece,
                value: catalogItem.id,
              })) ?? []
            }
            placeholder={t("Monitor aus Katalog auswählen")}
            value={item.catalogItemId ?? ""}
            onValueChange={selectDescription}
          />
        )}
        {!item.catalogItemId && freeTextSelected && (
          <InputDebounced
            aria-label={t("Monitor")}
            className="h-8 border-none shadow-none focus-visible:ring-0"
            disabled={disabled}
            placeholder={t("Freitext")}
            value={item.label}
            onChange={(event) => onManualChange(event.target.value)}
          />
        )}
      </div>
    </ETableCell>
  );
}

function ConsumablePriceItemDescriptionCell({
  catalog,
  disabled,
  item,
  onCatalogSelect,
  onManualSelect,
  onManualChange,
}: {
  catalog?: GfsCatalog;
  disabled?: boolean;
  item: GfsPriceItem;
  onCatalogSelect: (catalogConsumableId: string) => void;
  onManualSelect: () => void;
  onManualChange: (label: string) => void;
}) {
  const [freeTextSelected, setFreeTextSelected] = useState(
    !item.catalogConsumableId && Boolean(item.label),
  );

  const selectDescription = (value: string) => {
    if (value === FREE_TEXT_CATALOG_VALUE) {
      setFreeTextSelected(true);
      onManualSelect();
      return;
    }
    setFreeTextSelected(false);
    onCatalogSelect(value);
  };

  return (
    <ETableCell className="px-0">
      <div className="grid min-w-[14rem] gap-1 p-1">
        {!freeTextSelected && (
          <GfsCatalogCombobox
            filterOptions={catalogFilterOptions(
              catalog?.consumables.map((consumable) => consumable.type) ?? [],
            )}
            options={
              catalog?.consumables.map((consumable) => ({
                filterKey: consumable.type,
                label: formatGfsConsumableDisplayName(consumable),
                price: consumable.unitPrice,
                unit: consumable.unit,
                value: consumable.id,
              })) ?? []
            }
            placeholder={t("Verbrauchsmaterial aus Katalog auswählen")}
            value={item.catalogConsumableId ?? ""}
            onValueChange={selectDescription}
          />
        )}
        {!item.catalogConsumableId && freeTextSelected && (
          <InputDebounced
            aria-label={t("Verbrauchsmaterial")}
            className="h-8 border-none shadow-none focus-visible:ring-0"
            disabled={disabled}
            placeholder={t("Freitext")}
            value={item.label}
            onChange={(event) => onManualChange(event.target.value)}
          />
        )}
      </div>
    </ETableCell>
  );
}

type GfsCatalogComboboxOption = {
  filterKey?: string;
  label: string;
  price: number;
  unit: GfsUnitValue;
  value: string;
};

function GfsCatalogCombobox({
  disabled,
  filterOptions = [],
  options,
  placeholder,
  value,
  onValueChange,
}: {
  disabled?: boolean;
  filterOptions?: Array<{ value: string; label: string }>;
  options: GfsCatalogComboboxOption[];
  placeholder: string;
  value: string;
  onValueChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");
  const listId = useId();
  const selectedOption = options.find((option) => option.value === value);
  const visibleOptions = activeFilter
    ? options.filter((option) => option.filterKey === activeFilter)
    : options;

  const selectValue = (nextValue: string) => {
    onValueChange(nextValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-expanded={open}
          className="focus-visible:ring-ring/50 flex h-8 w-full items-center justify-between gap-2 rounded-none px-2 py-1 text-sm outline-none hover:bg-transparent focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          aria-controls={listId}
          role="combobox"
          type="button"
        >
          {selectedOption ? (
            <GfsCatalogOptionRow option={selectedOption} showMeta={false} />
          ) : (
            <span className="text-muted-foreground min-w-0 truncate">
              {placeholder}
            </span>
          )}
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(32rem,calc(100vw-2rem))] p-0"
      >
        <Command>
          <CommandInput placeholder={t("Suchen")} />
          {filterOptions.length > 0 && (
            <ToggleGroup
              className="my-1 w-full flex-wrap justify-start gap-1 px-1"
              size="sm"
              type="single"
              value={activeFilter}
              onValueChange={setActiveFilter}
            >
              {filterOptions.map((filter) => (
                <ToggleGroupItem
                  key={filter.value}
                  aria-label={filter.label}
                  className="px-2 py-1 text-xs"
                  value={filter.value}
                  variant="outline"
                >
                  {filter.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
          <CommandList id={listId}>
            <CommandEmpty>{t("Keine Einträge gefunden")}</CommandEmpty>
            <CommandItem
              value={`${FREE_TEXT_CATALOG_VALUE} ${t("Freitext")}`}
              onSelect={() => selectValue(FREE_TEXT_CATALOG_VALUE)}
            >
              <GfsCatalogOptionRow
                option={{
                  label: t("Freitext"),
                  price: 0,
                  unit: GfsUnit.None,
                  value: FREE_TEXT_CATALOG_VALUE,
                }}
              />
            </CommandItem>
            {visibleOptions.map((option) => (
              <CommandItem
                key={option.value}
                value={`${option.label} ${option.filterKey ?? ""}`}
                onSelect={() => selectValue(option.value)}
              >
                <GfsCatalogOptionRow option={option} />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function GfsCatalogOptionRow({
  option,
  showMeta = true,
}: {
  option: GfsCatalogComboboxOption;
  showMeta?: boolean;
}) {
  if (!showMeta) {
    return (
      <span className="min-w-0 truncate" title={option.label}>
        {option.label}
      </span>
    );
  }

  return (
    <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3">
      <span className="min-w-0 truncate" title={option.label}>
        {option.label}
      </span>
      {option.value === FREE_TEXT_CATALOG_VALUE ? (
        <span />
      ) : (
        <>
          <span className="text-right whitespace-nowrap tabular-nums">
            {formatCurrency(option.price)}
          </span>
          <span
            className="text-muted-foreground w-20 truncate text-right"
            title={option.unit}
          >
            {option.unit}
          </span>
        </>
      )}
    </span>
  );
}

function catalogFilterOptions(
  values: string[],
  formatLabel: (value: string) => string = (value) => value,
) {
  return [...new Set(values.filter(Boolean))]
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({ value, label: formatLabel(value) }));
}

function PriceItemCommentButton({
  comment,
  disabled,
  label,
  onChange,
}: {
  comment: string;
  disabled?: boolean;
  label: string;
  onChange: (comment: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(comment);
  const hasComment = Boolean(comment);
  return (
    <DialogTooltipButton
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) setDraft(comment);
        setOpen(nextOpen);
      }}
      tooltip={
        hasComment ? t("Kommentar bearbeiten") : t("Kommentar hinzufügen")
      }
      dialogTitle={t("Kommentar")}
      icon={<MessageSquareMore className="h-4 w-4" />}
      buttonProps={{
        "aria-label": hasComment
          ? t("Kommentar bearbeiten")
          : t("Kommentar hinzufügen"),
        className: cn(
          "mx-auto",
          hasComment && "text-primary hover:bg-primary/10 hover:text-primary",
        ),
        disabled: disabled && !open,
        size: "icon",
        type: "button",
        variant: "ghost",
      }}
      dialogContentProps={{ className: "sm:max-w-xl" }}
      content={
        <div className="space-y-2">
          <div className="text-sm font-medium">
            {label ? `${t("Kommentar")} ${label}` : t("Kommentar")}
          </div>
          <TextareaDebounced
            className="min-h-36"
            value={draft}
            onChange={(event) => {
              const nextComment = event.target.value;
              setDraft(nextComment);
              onChange(nextComment);
            }}
            placeholder={t("Kommentar")}
          />
        </div>
      }
    />
  );
}

function AssessmentSnapshotSummary({
  calculation,
  displayAssessmentId,
  processId,
}: {
  calculation: GfsCalculation;
  displayAssessmentId: string | null;
  processId: string;
}) {
  const { data: assessment } = useGfsAssessmentGetQuery(
    { id: displayAssessmentId ?? "" },
    { skip: !displayAssessmentId },
  );

  return (
    <section className="bg-card ring-border/50 rounded-lg px-6 py-5 shadow-sm ring-1">
      <h2 className="text-base font-semibold">{t("Basis-Bestandsaufnahme")}</h2>
      <div className="mt-4 grid gap-4 text-sm md:grid-cols-3">
        <SummaryValue label={t("Nummer")}>
          {displayAssessmentId ? (
            <NavLink
              className="text-primary underline underline-offset-4"
              to={`/processes/GFS/${processId}/assessments/${displayAssessmentId}`}
            >
              <DocumentNumberById
                documentId={displayAssessmentId}
                skip={!assessment?.immutable}
              />
            </NavLink>
          ) : (
            t("Nicht gewählt")
          )}
        </SummaryValue>
        <SummaryValue label={t("Thema")}>
          {hasAssessmentSnapshot(calculation)
            ? calculation.assessment.subject || t("Ohne Betreff")
            : t("Nicht gewählt")}
        </SummaryValue>
        <SummaryValue label={t("Snapshot")}>
          {formatSnapshotDate(calculation.assessment.takenAt)}
        </SummaryValue>
      </div>
    </section>
  );
}

function MeasureSettingsSection({
  disabled,
  measureCount,
  measureSettings,
  persistenceState,
  updateMeasureSettings,
}: {
  disabled?: boolean;
  measureCount: number;
  measureSettings: GfsMeasureSettings;
  persistenceState: PersistenceState;
  updateMeasureSettings: (settings: GfsMeasureSettings) => void;
}) {
  return (
    <section className="bg-card ring-border/50 space-y-4 rounded-lg px-6 py-5 shadow-sm ring-1">
      <SectionHeader
        title={t("Maßnahmenrhythmus")}
        description={t(
          "Die globale Anzahl der Maßnahmen steuert die verfügbaren Maßnahmengruppen. Die Anzahl je Gruppe und Preisposition wird unten festgelegt.",
        )}
        persistenceState={persistenceState}
      />
      <MeasureScheduleControl
        disabled={disabled}
        measureCount={measureCount}
        measureSettings={measureSettings}
        updateMeasureSettings={updateMeasureSettings}
      />
    </section>
  );
}

function MeasureScheduleControl({
  disabled,
  measureCount,
  measureSettings,
  updateMeasureSettings,
}: {
  disabled?: boolean;
  measureCount: number;
  measureSettings: GfsMeasureSettings;
  updateMeasureSettings: (settings: GfsMeasureSettings) => void;
}) {
  return (
    <div className="bg-muted/30 rounded-lg p-4">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_10rem] xl:items-center">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <InlineControl label={t("Anzahl")}>
            <IntNumberInput
              disabled={disabled}
              min={0}
              value={measureSettings.count}
              onChange={(count) =>
                updateMeasureSettings({ ...measureSettings, count })
              }
            />
          </InlineControl>
          <InlineControl label={t("je")}>
            <Select
              disabled={disabled}
              value={measureSettings.frequencyUnit || "none"}
              onValueChange={(value) =>
                updateMeasureSettings({
                  ...measureSettings,
                  frequencyUnit:
                    value === "none" ? "" : (value as GfsMeasurePeriodUnit),
                })
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("Keine Angabe")}</SelectItem>
                {MEASURE_PERIOD_UNITS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {t(unit.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </InlineControl>
          <InlineControl label={t("für")}>
            <IntNumberInput
              min={0}
              disabled={disabled || !measureSettings.durationUnit}
              value={measureSettings.durationValue}
              onChange={(durationValue) =>
                updateMeasureSettings({
                  ...measureSettings,
                  durationValue,
                })
              }
            />
          </InlineControl>
          <InlineControl label={t("Dauer")}>
            <Select
              disabled={disabled}
              value={measureSettings.durationUnit || "none"}
              onValueChange={(value) =>
                updateMeasureSettings({
                  ...measureSettings,
                  durationValue:
                    value === "none" ? 0 : measureSettings.durationValue,
                  durationUnit:
                    value === "none" ? "" : (value as GfsMeasurePeriodUnit),
                })
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("Keine Dauer")}</SelectItem>
                {MEASURE_PERIOD_UNITS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {t(unit.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </InlineControl>
        </div>
        <div className="bg-background/80 ring-border/50 flex items-center justify-between gap-3 rounded-md px-4 py-3 shadow-sm ring-1">
          <div className="text-muted-foreground text-sm font-medium">
            {t("Gesamte Anzahl an Maßnahmen")}
          </div>
          <div className="text-lg font-semibold">
            {formatNumber(measureCount, 0)}
          </div>
        </div>
      </div>
    </div>
  );
}

function PricingSettingsSection({
  disabled,
  persistenceState,
  pricingSettings,
  updatePricingSettings,
}: {
  disabled?: boolean;
  persistenceState: PersistenceState;
  pricingSettings: GfsPricingSettings;
  updatePricingSettings: (settings: GfsPricingSettings) => void;
}) {
  return (
    <section className="bg-card ring-border/50 space-y-4 rounded-lg px-6 py-5 shadow-sm ring-1">
      <SectionHeader
        title={t("Preisberechnung Personalaufwand")}
        persistenceState={persistenceState}
      />
      <div className="bg-muted/30 rounded-lg p-4">
        <h3 className="mb-3 text-sm font-semibold">{t("Zeiten")}</h3>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <InlineControl label={t("Preis je Minute")}>
            <FloatNumberInput
              disabled={disabled}
              value={pricingSettings.laborTravelFactor}
              fractions={4}
              onChange={(laborTravelFactor) =>
                updatePricingSettings(
                  pricingSettingsFromFactor(pricingSettings, laborTravelFactor),
                )
              }
            />
          </InlineControl>
          <InlineControl label={t("Zielumsatz pro Stunde")}>
            <div className="flex items-center gap-2">
              <FloatNumberInput
                disabled={disabled}
                value={pricingSettings.targetRevenuePerHour}
                onChange={(targetRevenuePerHour) =>
                  updatePricingSettings(
                    pricingSettingsFromTargetRevenue(
                      pricingSettings,
                      targetRevenuePerHour,
                    ),
                  )
                }
              />
              <span className="text-muted-foreground text-sm">€</span>
            </div>
          </InlineControl>
        </div>
      </div>
    </section>
  );
}

function InlineControl({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm font-medium whitespace-nowrap">
      <span>{label}</span>
      {children}
    </label>
  );
}

function SectionHeader({
  description,
  persistenceState,
  title,
}: {
  description?: string;
  persistenceState: PersistenceState;
  title: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description && (
          <p className="text-muted-foreground mt-1 text-sm">{description}</p>
        )}
      </div>
      <PersistenceStateInfo state={persistenceState} className="shrink-0" />
    </div>
  );
}

function SummaryValue({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  return (
    <div>
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function DeleteCalculationDialog({
  calculationId,
  disabled = false,
}: {
  calculationId: string;
  disabled?: boolean;
}) {
  const navigate = useNavigate();
  const [deleteCalculation, deleteState] = useGfsCalculationDeleteMutation();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button disabled={disabled} variant="outline">
          <Trash2 className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Kalkulation wirklich löschen?")}</DialogTitle>
        </DialogHeader>
        {deleteState.error && <RTKQueryErrorAlert error={deleteState.error} />}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={disabled || deleteState.isLoading}
            onClick={async () => {
              await deleteCalculation({ id: calculationId }).unwrap();
              toast.success(t("Kalkulation gelöscht"));
              navigate("..");
            }}
          >
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SelectAssessmentButton({
  calculation,
  calculationId,
  disabled = false,
  processId,
}: {
  calculation: GfsCalculation;
  calculationId: string;
  disabled?: boolean;
  processId: string;
}) {
  const [open, setOpen] = useState(false);
  const displayAssessmentId =
    calculation.sourceAssessmentId || calculation.assessment.sourceId;
  const { data: assessment } = useGfsAssessmentGetQuery(
    { id: displayAssessmentId ?? "" },
    { skip: !displayAssessmentId },
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2">
        {displayAssessmentId ? (
          <NavLink
            className="text-primary text-sm underline underline-offset-4"
            to={`/processes/GFS/${processId}/assessments/${displayAssessmentId}`}
          >
            <DocumentNumberById
              documentId={displayAssessmentId}
              skip={!assessment?.immutable}
            />
          </NavLink>
        ) : (
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={disabled}>
              {t("-- nicht gewählt --")}
            </Button>
          </DialogTrigger>
        )}
        {displayAssessmentId && (
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={disabled}>
              {t("Ändern")}
            </Button>
          </DialogTrigger>
        )}
      </div>
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle>{t("Bestandsaufnahmen")}</DialogTitle>
        </DialogHeader>
        <CreateFromAssessment
          calculationId={calculationId}
          disabled={disabled}
          onLoaded={() => setOpen(false)}
          processId={processId}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreateFromAssessment({
  calculationId,
  disabled,
  onLoaded,
  processId,
}: {
  calculationId: string;
  disabled?: boolean;
  onLoaded: () => void;
  processId: string;
}) {
  return (
    <section className="rounded-md border p-6">
      <h2 className="text-base font-semibold">
        {t("Daten auf Basis einer Bestandsaufnahme laden")}
      </h2>
      <p className="text-muted-foreground mt-1 text-sm">
        {t("Kopiert die Daten der gewählten Bestandsaufnahme.")}
      </p>
      <div className="mt-6">
        <AssessmentsTable
          calculationId={calculationId}
          disabled={disabled}
          onLoaded={onLoaded}
          processId={processId}
        />
      </div>
    </section>
  );
}

function AssessmentsTable({
  calculationId,
  disabled,
  onLoaded,
  processId,
}: {
  calculationId: string;
  disabled?: boolean;
  onLoaded: () => void;
  processId: string;
}) {
  const {
    data: calculation,
    error: calculationError,
    isLoading: calculationLoading,
  } = useGfsCalculationGetQuery({ id: calculationId });
  const {
    data: assessments,
    error,
    isLoading,
  } = useGfsAssessmentListQuery(
    {
      company: { active: true, values: [calculation?.companyId ?? ""] },
      customer: { active: true, values: [calculation?.customerId ?? ""] },
    },
    { skip: !calculation?.companyId || !calculation?.customerId },
  );

  if (calculationError) return <RTKQueryErrorAlert error={calculationError} />;
  if (error) return <RTKQueryErrorAlert error={error} />;
  if (calculationLoading || isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead className="w-56">{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead className="w-56">{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...(assessments?.items ?? [])].sort(byCreatedAtAndId).map((item) => (
          <TableRow key={item.id}>
            <TableCell>
              <DocumentNumberById documentId={item.id} skip={!item.immutable} />
            </TableCell>
            <TableCell>{formatDate(item.createdAt)}</TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavLink
                  to={`/processes/GFS/${processId}/assessments/${item.id}`}
                >
                  <Button variant="outline" size="sm">
                    <Search className="h-4 w-4" />
                  </Button>
                </NavLink>
                <LoadAssessmentButton
                  calculationId={calculationId}
                  assessmentId={item.id}
                  disabled={disabled}
                  onLoaded={onLoaded}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function LoadAssessmentButton({
  calculationId,
  assessmentId,
  disabled = false,
  onLoaded,
}: {
  calculationId: string;
  assessmentId: string;
  disabled?: boolean;
  onLoaded: () => void;
}) {
  const [loadAssessment, { isLoading, error }] =
    useGfsCalculationLoadAssessmentMutation();

  return (
    <div className="flex items-center gap-2">
      {error && <RTKQueryErrorAlert error={error} />}
      <Button
        onClick={() => {
          loadAssessment({ id: calculationId, assessmentId })
            .unwrap()
            .then(() => {
              onLoaded();
              toast.success(t("Bestandsaufnahme übernommen"));
            })
            .catch(() => {
              toast.error(t("Fehler"), {
                description: t(
                  "Die Bestandsaufnahme konnte nicht übernommen werden.",
                ),
              });
            });
        }}
        disabled={disabled || isLoading}
        size="sm"
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!isLoading && (
          <>
            <Copy className="mr-3 h-4 w-4" />
            <span>{t("Daten Übernehmen")}</span>
          </>
        )}
      </Button>
    </div>
  );
}

function hasAssessmentSnapshot(calculation: GfsCalculation) {
  return Boolean(
    calculation.sourceAssessmentId ||
    calculation.assessment.sourceId ||
    isSnapshotDate(calculation.assessment.takenAt),
  );
}

function formatSnapshotDate(value?: string) {
  if (!isSnapshotDate(value)) {
    return t("Nicht geladen");
  }
  return new Date(value).toLocaleString();
}

function isSnapshotDate(value?: string): value is string {
  return Boolean(value && !value.startsWith("0001-01-01"));
}

function toPutRequest(calculation: GfsCalculation): PutGfsCalculationRequest {
  return {
    id: calculation.id,
    measureSettings: calculation.measureSettings,
    pricingSettings: calculation.pricingSettings,
    measures: calculation.measures,
    priceItems: calculation.pricing.items,
  };
}

function emptyPutRequest(id: string): PutGfsCalculationRequest {
  return {
    id,
    measureSettings: {
      count: 0,
      frequencyUnit: "",
      durationValue: 0,
      durationUnit: "",
    },
    pricingSettings: {
      laborTravelFactor: 0,
      targetRevenuePerHour: 0,
    },
    measures: [],
    priceItems: [],
  };
}

function toOptimisticCalculation(
  calculation: GfsCalculation,
  request: PutGfsCalculationRequest,
): GfsCalculation {
  return {
    ...calculation,
    measureSettings: request.measureSettings,
    pricingSettings: request.pricingSettings,
    measures: request.measures,
    pricing: {
      ...calculation.pricing,
      items: request.priceItems,
    },
  };
}

function defaultFlexibleMeasure(kind: GfsMeasureKind): GfsMeasure {
  return {
    id: v4(),
    kind,
    label:
      kind === "INITIAL"
        ? t("Erstmaßnahme")
        : kind === "FINAL"
          ? t("Letzte Maßnahme")
          : t("Zwischenmaßnahme"),
    useUnitPrice: false,
    repeatCount: 1,
    repeatIntervalValue: 1,
    repeatIntervalUnit: kind === "INTERMEDIATE" ? "WEEK" : "",
    technicians: 0,
    duration: 0,
    priceItems: [],
    eradicationCheck: false,
  };
}

function defaultFlexiblePriceItem(): GfsPriceItem {
  return {
    id: v4(),
    kind: "other",
    pricingType: "CALCULATED",
    label: "",
    comment: "",
    percentage: 0,
    quantity: 1,
    unit: GfsUnit.Piece,
    unitPrice: 0,
    unitPriceManual: true,
    applyMeasureFactor: false,
    applyMeasureFactorManual: true,
    measureFactorManual: false,
    measureFactorOverride: 0,
    total: 0,
  };
}

function defaultFlexiblePriceItemForKind(
  kind: GfsPriceItemKind,
  id: string,
): GfsPriceItem {
  const item = defaultFlexiblePriceItem();
  return {
    ...item,
    id,
    kind,
    label: kind === "labor" ? t("Arbeitszeit") : item.label,
    unit:
      kind === "surcharge"
        ? GfsUnit.Percent
        : kind === "labor" || kind === "travel"
          ? GfsUnit.Minute
          : GfsUnit.Piece,
    unitPriceManual: kind !== "labor" && kind !== "travel",
  };
}

function clampMeasureCount(value: number, maximum: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(Math.max(Math.trunc(value), 0), Math.max(maximum, 0));
}

function isLaborTravelPriceItem(item: GfsPriceItem): boolean {
  return item.kind === "labor" || item.kind === "travel";
}

function displayedPriceItemUnitPrice(
  item: GfsPriceItem,
  pricingSettings: GfsPricingSettings,
): number {
  if (!isLaborTravelPriceItem(item) || item.unitPriceManual) {
    return item.unitPrice;
  }
  if (normalizeGfsUnit(item.unit) === GfsUnit.Hour) {
    return pricingSettings.targetRevenuePerHour;
  }
  return pricingSettings.laborTravelFactor;
}

function pricingSettingsFromFactor(
  settings: GfsPricingSettings,
  laborTravelFactor: number,
): GfsPricingSettings {
  if (laborTravelFactor <= 0) {
    return {
      ...settings,
      laborTravelFactor: 1.5,
      targetRevenuePerHour: 90,
    };
  }
  return {
    ...settings,
    laborTravelFactor,
    targetRevenuePerHour: roundPricingNumber(laborTravelFactor * 60, 2),
  };
}

function pricingSettingsFromTargetRevenue(
  settings: GfsPricingSettings,
  targetRevenuePerHour: number,
): GfsPricingSettings {
  if (targetRevenuePerHour <= 0) {
    return {
      ...settings,
      laborTravelFactor: 1.5,
      targetRevenuePerHour: 90,
    };
  }
  return {
    ...settings,
    laborTravelFactor: roundPricingNumber(targetRevenuePerHour / 60, 6),
    targetRevenuePerHour: roundPricingNumber(targetRevenuePerHour, 2),
  };
}

function roundPricingNumber(value: number, fractions: number): number {
  return Number(value.toFixed(fractions));
}

function priceItemUnitOptions(item: GfsPriceItem): GfsUnitValue[] {
  const currentUnit = normalizeGfsUnit(item.unit);
  const options = isLaborTravelPriceItem(item)
    ? TIME_PRICE_ITEM_UNIT_OPTIONS
    : PRICE_ITEM_UNIT_OPTIONS;
  if (!currentUnit || options.includes(currentUnit)) {
    return options;
  }
  return [currentUnit, ...options];
}

function convertTimePriceItemQuantity(
  quantity: number,
  currentUnit: GfsUnitValue,
  nextUnit: GfsUnitValue,
): number {
  if (currentUnit === GfsUnit.Minute && nextUnit === GfsUnit.Hour) {
    return quantity / 60;
  }
  if (currentUnit === GfsUnit.Hour && nextUnit === GfsUnit.Minute) {
    return quantity * 60;
  }
  return quantity;
}

function updateSavingSections(
  current: Record<CalculationPersistenceSection, PersistenceState>,
  nextState: PersistenceState,
): Record<CalculationPersistenceSection, PersistenceState> {
  return {
    measures:
      current.measures === PersistenceState.SAVING
        ? nextState
        : current.measures,
    pricing:
      current.pricing === PersistenceState.SAVING ? nextState : current.pricing,
    priceItems:
      current.priceItems === PersistenceState.SAVING
        ? nextState
        : current.priceItems,
  };
}

function byCreatedAtAndId(
  left: { createdAt: string; id: string },
  right: { createdAt: string; id: string },
) {
  const createdAtOrder = right.createdAt.localeCompare(left.createdAt);
  if (createdAtOrder !== 0) return createdAtOrder;
  return right.id.localeCompare(left.id);
}

function UpdatingValue({
  children,
  isUpdating,
}: {
  children: ReactNode;
  isUpdating: boolean;
}) {
  return (
    <span className="flex w-full items-center justify-end gap-1">
      {isUpdating && (
        <Loader2
          aria-hidden="true"
          className="text-muted-foreground h-4 w-4 shrink-0 animate-spin"
        />
      )}
      {children}
    </span>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(value ?? 0);
}

function formatNumber(value: number, fractions: number) {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractions,
  }).format(value ?? 0);
}
